import { createElement } from 'react';
import { createRoot } from 'react-dom/client';
import { toJpeg } from 'html-to-image';
import { InternalUiRenderer } from '@erp/json-renderer/react';
import type { InternalUiSpec } from '@erp/json-renderer/react';

/** Width the preview renders at — a representative full-width chat dashboard. */
const PREVIEW_WIDTH_PX = 880;

/**
 * Render an internal-UI spec OFF-SCREEN and screenshot it, so the chat agent can
 * see what it built before presenting it to the user. Mounts the real
 * `InternalUiRenderer` (same @erp/ui + @erp/viz components the user gets) into a
 * detached, off-viewport node, waits for layout + async charts to settle, then
 * captures a JPEG via html-to-image.
 *
 * Returns base64 (no data: prefix) so it drops straight into the agent's
 * multimodal media block. The node is always cleaned up.
 */
export async function renderSpecToImage(
  spec: InternalUiSpec,
  state: Record<string, unknown>,
): Promise<{ imageData: string; mimeType: 'image/jpeg' }> {
  const container = document.createElement('div');
  // Off-screen but still laid out (not display:none) so widths resolve and
  // charts can measure their container. Excluded from any page screenshot.
  container.setAttribute('data-capture-exclude', 'true');
  Object.assign(container.style, {
    position: 'fixed',
    left: '-100000px',
    top: '0',
    width: `${PREVIEW_WIDTH_PX}px`,
    background: '#ffffff',
    padding: '16px',
    zIndex: '-1',
    pointerEvents: 'none',
  });
  document.body.appendChild(container);

  const root = createRoot(container);
  try {
    root.render(createElement(InternalUiRenderer, { spec, state }));
    // Two frames for React paint, then a beat for Vega/react-vega to draw.
    await new Promise<void>((resolve) =>
      requestAnimationFrame(() => requestAnimationFrame(() => resolve())),
    );
    await new Promise<void>((resolve) => setTimeout(resolve, 800));

    const dataUrl = await toJpeg(container, {
      quality: 0.9,
      backgroundColor: '#ffffff',
      pixelRatio: 1.5,
      cacheBust: true,
    });
    return {
      imageData: dataUrl.replace(/^data:image\/jpeg;base64,/, ''),
      mimeType: 'image/jpeg',
    };
  } finally {
    root.unmount();
    container.remove();
  }
}
